import React from 'react';
import { IconsBox, PdfCard, PdfHeader, PdfPara } from './style';
import { Box, CircularProgress, Divider, IconButton, useTheme } from '@mui/material';
import MoreVerticle from '../../icons/MoreVerticle';
import { iconMapping } from './iconMapping';
import { faFile } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { post } from '../../api';

/**
 * DocumentCard
 *
 * A card component to display document/pdf-file/doc-file information with an icon, title, and description.
 * It includes a menu icon button for additional options.
 *
 * @param {Object} props - Component props
 * @param {Object} props.docData - Document data containing icon, title, and description.
 *
 * @returns {JSX.Element} - A styled card displaying document information.
 */
function DocumentCard({ docData, ...props }) {
    const [downloadStarted, setDownloadStarted] = React.useState(false);
    const theme = useTheme();
    const getColor = (file) => {
        const extension = file.fileExtension || file.FileExtension;
        const color = iconMapping[extension]?.color;
        return color ? color : '#413ab4';
    };

    const getIcon = (file) => {
        const extension = file.fileExtension || file.FileExtension;
        const icon = iconMapping[extension]?.icon;
        return icon ? icon : faFile;
    };

    const getCustom = (file) => {
        const extension = file.fileExtension || file.FileExtension;
        const custom = iconMapping[extension]?.custom;
        return custom;
    };

    const download = async (url, name) => {
        const a = document.createElement('a');
        a.href = url;
        a.download = name; // Fallback filename
        a.style.display = 'none';
        document.body.appendChild(a);

        a.click();
        document.body.removeChild(a);
        setDownloadStarted(false);
    };

    function downloadBase64File(base64String, fileName) {
        // Convert Base64 string to a Blob
        let byteCharacters = atob(base64String);
        let byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        let byteArray = new Uint8Array(byteNumbers);
        let blob = new Blob([byteArray]);

        // Create a download link
        let link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName; // Set the filename
        document.body.appendChild(link);

        // Trigger the download
        link.click();

        // Cleanup
        document.body.removeChild(link);
        setDownloadStarted(false);
    }

    const handleDocumentClick = () => {
        setDownloadStarted(true);
        if (!docData.s3File) {
            post('custom/getDocumentPublicLinks', { recordId: docData?.latestPublishedVersionId }).then((result) => {
                if (result.isSuccess) {
                    //const newTab = window.open(result.data, '_blank');
                    //downloadBase64File(result.data, docData.title + '.' + docData.fileExtension);
                    // if (!newTab) {
                    //     alert('Please enable popups for this site to view the files.');
                    // }

                    download(result.data, docData.title + '.' + docData.fileExtension);
                }
            });
        } else {
            post('s3/getDownloadURL', { key: docData.S3Key, filename: docData.Title + '.' + docData.FileExtension }).then((result) => {
                if (result.isSuccess) {
                    download(result.data, docData.Title + '.' + docData.FileExtension);
                }
            });
        }
    };

    const getTitle = (doc) => {
        return doc.clientTitle || doc.ClientTitle || docData.title || docData.Title;
    };

    return (
        <PdfCard {...props} onClick={handleDocumentClick}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <IconsBox className="iconBox">{getCustom(docData) ? getCustom(docData) : <FontAwesomeIcon size="4x" color={getColor(docData)} icon={getIcon(docData)} />}</IconsBox>
                {downloadStarted && <CircularProgress size={20} sx={{ mt: '1rem', mr: '1rem' }} />}
            </Box>

            <Divider />

            <Box
                padding={{
                    xs: theme.typography.generic.xSmall,
                    sm: theme.typography.generic.xLarge,
                    cursor: 'pointer',
                }}
            >
                <PdfHeader title={getTitle(docData)}>{getTitle(docData)}</PdfHeader>
                <PdfPara title={docData.fileType}>{docData.fileType || docData.FileExtension}</PdfPara>
            </Box>
        </PdfCard>
    );
}

export default DocumentCard;
